import { getTabScope } from "../../stores/auth.store";
import apiService from "./apiService";
import { ENDPOINTS } from "../Endpoints";

/**
 * Authentication Service
 * Example of how to create service modules for specific domains
 * This makes it easy to add new API calls - just add methods here!
 */
class AuthService {
  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {boolean} rememberMe - Remember user session
   * @returns {Promise}
   */
  async login(email, password, rememberMe = false) {
    return apiService.post(ENDPOINTS.AUTH.LOGIN, {
      email,
      password,
      rememberMe,
    });
  }

  /**
   * Login admin
   * @param {string} email - Admin email
   * @param {string} password - Admin password
   * @param {boolean} rememberMe - Remember admin session
   * @returns {Promise}
   */
  async adminLogin(email, password, rememberMe = false) {
    return apiService.post(ENDPOINTS.AUTH.ADMIN_LOGIN, {
      email,
      password,
      rememberMe,
    });
  }

  /**
   * Refresh access token using httpOnly cookie
   * @returns {Promise}
   */
  async refreshTokens() {
    // Refresh this tab's own role; other tabs may be signed in as other roles.
    return apiService.post(ENDPOINTS.AUTH.REFRESH_TOKENS, { scope: getTabScope() });
  }

  /**
   * Logout user (clears httpOnly cookie)
   * @returns {Promise}
   */
  async logout() {
    return apiService.post(ENDPOINTS.AUTH.LOGOUT, { scope: getTabScope() });
  }

  /**
   * Register new user
   * @param {object} userData - User registration data
   * @returns {Promise}
   */
  async signup(userData) {
    return apiService.post(ENDPOINTS.AUTH.SIGNUP, userData);
  }

  /**
   * Get user account information
   * @returns {Promise}
   */
  async getAccount() {
    return apiService.get(ENDPOINTS.AUTH.ACCOUNT);
  }

  /**
   * Update user account
   * @param {object} accountData - Account data to update
   * @returns {Promise}
   */
  async updateAccount(accountData) {
    return apiService.put(ENDPOINTS.AUTH.UPDATE_ACCOUNT, accountData);
  }

  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise}
   */
  async changePassword(currentPassword, newPassword) {
    return apiService.post(ENDPOINTS.AUTH.CHANGE_PASSWORD, {
      currentPassword,
      newPassword,
    });
  }
}

// Create and export a singleton instance
const authService = new AuthService();

export default authService;
